import { useEffect, useState } from 'react';
import { fetchAsset } from '@/api/dataroom';
import type { ThemeDesign } from '@/api/types';
import { typography } from './tokens';

/**
 * A hub's uploaded fonts and images (OL-23, design doc sections 3 and 5). The bytes come through the room's
 * API client, with the session bearer. A font becomes a FontFace from those bytes, which the CSP's font-src
 * has no say over, because no font URL is ever fetched. An image becomes a blob: URL, which img-src permits. A
 * failure is never an applicant's problem: the room's own mark and the system font are right there.
 */

/** A hub family, then the room's own stack, so a Latin-only brand font leaves Devanagari and Arabic readable. */
export function familyStack(family: string): string {
  return `"${family}", ${typography.fontFamily}`;
}

/** The CSS variables and the radius attribute. With no design, none of them are set. */
export function applyDesignVars(design: ThemeDesign | null | undefined): void {
  const root = document.documentElement;
  const set = (name: string, family?: string) => {
    if (family) root.style.setProperty(name, familyStack(family));
    else root.style.removeProperty(name);
  };
  set('--ol-font-body', design?.bodyFamily);
  set('--ol-font-heading', design?.headingFamily);
  if (design?.radius && design.radius !== 'DEFAULT') root.dataset.radius = design.radius;
  else delete root.dataset.radius;
}

/** Loads the hub's fonts into the document. Returns what removes them again. */
export function loadHubFonts(design: ThemeDesign | null | undefined): () => void {
  const fonts = design?.fonts ?? [];
  if (fonts.length === 0 || typeof FontFace === 'undefined') return () => {};
  let current = true;
  const added: FontFace[] = [];
  for (const font of fonts) {
    fetchAsset(font.assetId)
      .then(({ bytes }) =>
        new FontFace(font.family, bytes, {
          weight: String(font.weight ?? 400),
          style: font.style ?? 'normal',
          display: 'swap',
        }).load(),
      )
      .then((face) => {
        if (!current) return;
        document.fonts.add(face);
        added.push(face);
      })
      .catch(() => {
        // A font the browser's own sanitiser refuses: the stack after it takes over.
      });
  }
  return () => {
    current = false;
    for (const face of added) document.fonts.delete(face);
  };
}

/** A brand image's blob: URL, or null until it has loaded (and if it cannot). */
export function useAssetUrl(assetId: string | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!assetId) {
      setUrl(null);
      return undefined;
    }
    let current = true;
    let made: string | null = null;
    fetchAsset(assetId)
      .then(({ bytes, mediaType }) => {
        if (!current) return;
        made = URL.createObjectURL(new Blob([bytes], { type: mediaType }));
        setUrl(made);
      })
      .catch(() => setUrl(null));
    return () => {
      current = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [assetId]);
  return url;
}
